import { useRef, useState, useEffect } from "react";
import { X, Upload, CheckCircle, AlertTriangle, Loader2, Eye } from "lucide-react";
import { getDocuments, uploadDocument, deleteDocument, fetchDocumentBlobUrl, syncDocumentsFromSalesforce, type CvDocument } from "@/services/universityService";
import FilePreviewModal from "@/pages/portal/components/FilePreviewModal";

const DEFAULT_ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png";
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

function resolveAcceptTypes(documentType: string | null | undefined): string {
  if (!documentType || !documentType.trim()) return DEFAULT_ACCEPTED_TYPES;
  // Format from DB: ".docx;.jpeg;.jpg;.pdf;.png" — convert semicolons to commas
  const normalized = documentType
    .split(";")
    .map((t) => t.trim())
    .filter(Boolean)
    .join(",");
  return normalized || DEFAULT_ACCEPTED_TYPES;
}

function formatLabel(documentType: string | null | undefined): string {
  if (!documentType || !documentType.trim()) return "PDF, JPG, PNG";
  return documentType
    .split(";")
    .map((t) => t.trim().replace(/^\./, "").toUpperCase())
    .filter(Boolean)
    .join(", ");
}

interface UploadState {
  file: File | null;
  progress: number;
  uploading: boolean;
  done: boolean;
  error: string | null;
  dbId: number | null;
  previewUrl: string | null;
  previewMimeType: string | null;
  previewLoading: boolean;
}

interface DocumentCardProps {
  doc: CvDocument;
  appNumber: string;
  onUploadDone: (docId: string, success: boolean) => void;
}

function DocumentCard({ doc, appNumber, onUploadDone }: DocumentCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [state, setState] = useState<UploadState>({
    file: null,
    progress: 0,
    uploading: false,
    done: doc.public_url != null || doc.status === "Approved",
    error: null,
    dbId: doc.db_id ?? null,
    previewUrl: null,
    previewMimeType: null,
    previewLoading: false,
  });

  useEffect(() => {
    return () => {
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    };
  }, [state.previewUrl]);

  const handleViewClick = async () => {
    if (state.previewUrl) { setPreviewOpen(true); return; }
    const dbId = state.dbId;
    if (!dbId) return;
    setState((s) => ({ ...s, previewLoading: true }));
    try {
      const { blobUrl, mimeType } = await fetchDocumentBlobUrl(dbId);
      setState((s) => ({ ...s, previewUrl: blobUrl, previewMimeType: mimeType || null, previewLoading: false }));
      setPreviewOpen(true);
    } catch (err) {
      console.warn("[DocumentCard] preview fetch failed for db_id=%s:", dbId, err);
      setState((s) => ({ ...s, previewLoading: false }));
    }
  };

  const maxBytes = doc.document_maximum_size != null
    ? doc.document_maximum_size * 1024 * 1024
    : MAX_SIZE_BYTES;
  const maxMbDisplay = Math.round(maxBytes / 1024 / 1024);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > maxBytes) {
      setState((s) => ({ ...s, error: `File exceeds the ${maxMbDisplay} MB limit.` }));
      return;
    }

    setState((s) => ({ ...s, file, progress: 0, uploading: true, done: false, error: null, previewUrl: null, previewMimeType: null }) as UploadState);

    try {
      const result = await uploadDocument(appNumber, doc.setting_record_id, file, (pct) =>
        setState((s) => ({ ...s, progress: pct })),
        doc.document_name,
      );
      setState((s) => ({
        ...s,
        uploading: false,
        done: true,
        progress: 100,
        dbId: result.document_id ?? s.dbId,
        previewUrl: URL.createObjectURL(file),
        previewMimeType: file.type || null,
      }));
      onUploadDone(doc.id, true);
    } catch {
      setState((s) => ({ ...s, uploading: false, error: "Upload failed. Please try again." }));
      onUploadDone(doc.id, false);
    }
  };

  const handleRemove = async () => {
    const dbId = state.dbId;
    const previousState = { ...state };

    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    setState({ file: null, progress: 0, uploading: false, done: false, error: null, dbId: null, previewUrl: null, previewMimeType: null, previewLoading: false });
    if (inputRef.current) inputRef.current.value = "";
    onUploadDone(doc.id, false);

    if (dbId) {
      try {
        await deleteDocument(appNumber, dbId);
      } catch (err) {
        console.error("Failed to delete document from server:", err);
        // Restore previous state so the user knows deletion failed
        setState({ ...previousState });
        onUploadDone(doc.id, true);
      }
    }
  };

  const borderClass = state.done
    ? "border-emerald-400 bg-emerald-50/30"
    : state.error
    ? "border-red-300 bg-red-50/20"
    : "border-slate-200";

  return (
    <div className={`rounded-2xl border transition-all duration-200 bg-white p-5 space-y-3 ${borderClass}`}>
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1 flex gap-1 items-center">
          {doc.document_name}
          {doc.required && <span className="text-red-500">*</span>}
        </label>
        {(state.file || state.done) && !state.uploading && (
          <button
            type="button"
            onClick={handleRemove}
            className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-0.5 rounded-lg transition-all"
          >
            <X className="w-3 h-3" /> Remove
          </button>
        )}
      </div>

      {state.done ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-white px-3 py-2.5">
          <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
          <span className="text-xs font-black text-slate-800 truncate flex-1">
            {state.file?.name ?? "Previously uploaded"}
          </span>
          {(state.dbId || state.previewUrl) && (
            <button
              type="button"
              onClick={handleViewClick}
              disabled={state.previewLoading}
              className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-500 hover:text-[#001B44] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-lg transition-all shrink-0 disabled:opacity-50"
            >
              {state.previewLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
              View
            </button>
          )}
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.7)] shrink-0" />
        </div>
      ) : state.uploading ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Uploading… {state.progress}%
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5">
            <div
              className="bg-[#001B44] h-1.5 rounded-full transition-all duration-200"
              style={{ width: `${state.progress}%` }}
            />
          </div>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl p-5 cursor-pointer hover:border-[#001B44]/40 hover:bg-slate-50 transition-all">
          <Upload className="w-5 h-5 text-slate-400" />
          <span className="text-xs font-bold text-slate-500">
            Click to upload · {formatLabel(doc.document_type)} · max {maxMbDisplay} MB
          </span>
          <input
            ref={inputRef}
            type="file"
            accept={resolveAcceptTypes(doc.document_type)}
            onChange={handleChange}
            className="sr-only"
          />
        </label>
      )}

      {state.error && (
        <div className="flex items-center gap-2 text-xs font-bold text-red-600">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          {state.error}
        </div>
      )}

      {doc.description && !state.done && (
        <p className="text-[11px] text-slate-500 font-medium ml-1">{doc.description}</p>
      )}
      {doc.message && (
        <p className="text-[11px] text-amber-600 font-medium ml-1">{doc.message}</p>
      )}
      {previewOpen && state.previewUrl && (
        <FilePreviewModal
          url={state.previewUrl}
          fileName={state.file?.name ?? doc.document_name}
          mimeType={state.previewMimeType ?? undefined}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

interface DocumentsProps {
  appNumber: string;
  onRequirementChange?: (met: boolean) => void;
}


export default function Documents({ appNumber, onRequirementChange }: DocumentsProps) {
  const [docs, setDocs] = useState<CvDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!appNumber) return;

    let cancelled = false;
    setLoading(true);
    setFetchError(null);

    const loadFromDb = () => {
      getDocuments(appNumber)
        .then((res) => {
          if (cancelled) return;
          setDocs(res.documents);
          const alreadyUploaded = new Set(
            res.documents.filter((d) => d.public_url != null).map((d) => d.id)
          );
          setUploaded(alreadyUploaded);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("[Documents] fetch error:", err);
          setFetchError("Could not load document requirements. You may still upload below.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    syncDocumentsFromSalesforce(appNumber)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) loadFromDb();
      });

    return () => { cancelled = true; };
  }, [appNumber]);

  const handleUploadDone = (docId: string, success: boolean) => {
    setUploaded((prev) => {
      const next = new Set(prev);
      if (success) next.add(docId);
      else next.delete(docId);
      return next;
    });
  };

  const displayRequired = docs.filter((d: CvDocument) => d.required);
  const displayOptional = docs.filter((d: CvDocument) => !d.required);

  useEffect(() => {
    if (fetchError) {
      onRequirementChange?.(false);
      return;
    }
    const requiredIds = displayRequired.map((d: CvDocument) => d.id);
    const met = requiredIds.length === 0 || requiredIds.every((id: string) => uploaded.has(id));
    onRequirementChange?.(met);
  }, [docs, uploaded, fetchError]);


  return (
    <div className="space-y-8 md:space-y-12">
      <div>
        <h2 className="text-2xl md:text-4xl font-serif font-black text-[#001B44] leading-tight text-left">
          Documents
        </h2>
        <p className="text-slate-900 mt-2 md:mt-4 text-base md:text-lg font-medium max-w-2xl text-left">
          Please upload your required and optional supporting documents.
        </p>
      </div>

      {fetchError && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
          {fetchError}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-10">
          {displayRequired.length > 0 && (
            <section className="space-y-4">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#001B44]" />
                Required Documents
              </h4>
              {displayRequired.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  appNumber={appNumber}
                  onUploadDone={handleUploadDone}
                />
              ))}
            </section>
          )}

          {displayOptional.length > 0 && (
            <section className="space-y-4">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                Optional Documents
              </h4>
              {displayOptional.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  appNumber={appNumber}
                  onUploadDone={handleUploadDone}
                />
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
